/** Closed, I/O-free tuples shared by domain types and wire validators. */
export const BAG_PHOTO_POLICIES = ['OFF', 'OPTIONAL', 'MANDATORY'] as const;

export const BAG_PHOTO_OUTCOMES = ['TAKEN', 'SKIPPED', 'CAMERA_UNAVAILABLE'] as const;

export const ALLERGEN_CODES = [
  'GLUTEN',
  'CRUSTACEANS',
  'EGGS',
  'FISH',
  'PEANUTS',
  'SOYBEANS',
  'MILK',
  'NUTS',
  'CELERY',
  'MUSTARD',
  'SESAME',
  'SULPHITES',
  'LUPIN',
  'MOLLUSCS',
] as const;

export const CATALOGUE_AVAILABILITIES = ['IN_STOCK', 'SOLD_OUT_TODAY', 'HIDDEN'] as const;

export const ACCOUNTING_STATES = ['NOT_APPLICABLE', 'PENDING', 'EXPORTED', 'FAILED'] as const;

export const CHECKOUT_REFUSAL_CAUSES = [
  /** The chosen window no longer fits the travel estimate plus margins. */
  'WINDOW_NOT_OFFERABLE',
  /** The chosen window's start is already behind the platform clock. */
  'WINDOW_PASSED',
  /** Ordering is closed for the property or the morning (WEB-003). */
  'ORDERING_CLOSED',
  /** The basket is under the configured minimum (PAY-023). */
  'MINIMUM_BASKET',
  /** The allocator found no bakery able to serve this apartment (ALC-006). */
  'NO_BAKERY',
  /** The apartment is outside every counter's reach at quote time. */
  'NOT_SERVABLE',
  /** An operator or the counter paused service for this morning (BAK-006). */
  'SERVICE_PAUSED',
  /** Mode B: the guest has to pick a shop and had not (ALC-010). */
  'GUEST_CHOICE_REQUIRED',
  /** The delivery provider would not quote the leg (RTE-020). */
  'COURIER_QUOTE_REFUSED',
  /** The courier quoted the delivery above the administered ceiling (RTE-027). */
  'COURIER_QUOTE_ABOVE_CEILING',
  /** The gateway refused the authorization, before any order was written. */
  'PAYMENT_DECLINED',
] as const;

export const DELIVERY_CLOSED_OUTCOMES = ['NO_COURIER', 'FAILED', 'COMPLETED'] as const;

export const DELIVERY_JOB_STATUSES = [
  'PENDING',
  'QUOTED',
  'CREATED',
  'COURIER_ASSIGNED',
  'PICKED_UP',
  'ARRIVING',
  /**
   * The delivery failed after collection and the rider is bringing the bag
   * back to the counter (founder ruling #21, 5 October 2026). Only a provider
   * that STATES a return leg reports it; the terminal FAILED still follows.
   */
  'RETURNING',
  'DELIVERED',
  'CANCELLED',
  'FAILED',
] as const;

export const DEVICE_KINDS = ['SUNMI', 'BROWSER'] as const;

export const DIETARY_TAGS = [
  'VEGETARIAN',
  'VEGAN',
  'GLUTEN_FREE',
  'LACTOSE_FREE',
  'HALAL',
] as const;

export const DOCUMENT_FAMILIES = [
  'GUEST_FEES',
  'GUEST_PRODUCTS',
  'BAKERY_COMMISSION',
  'PARTNER_SELF_BILL',
  'PARTNER_STATEMENT',
] as const;

export const DOCUMENT_NATURES = ['INVOICE', 'CREDIT_NOTE', 'STATEMENT'] as const;

export const DOCUMENT_STATES = ['DRAFT', 'ISSUED', 'CANCELLED'] as const;

export const DOOR_POSITIONS = ['LEFT', 'RIGHT', 'CENTRE', 'FACING'] as const;

export const GUEST_STAGES = [
  'ORDER_PLACED',
  'ORDER_CONFIRMED',
  'PREPARING',
  'ORDER_READY',
  'FINDING_COURIER',
  'COURIER_ASSIGNED',
  'ON_THE_WAY',
  'ARRIVING',
  'DELIVERED',
] as const;

export const INCIDENT_CAUSES = [
  'ACCESS_FAILURE',
  'CHARGEBACK',
  'GOODWILL',
  'PROPERTY_REMOVED',
] as const;

export const ORDER_CANCELLED_CAUSES = [
  /** Offered, and refused or unanswered by every candidate (ALC-006). */
  'CASCADE_EXHAUSTED',
  /** A paid order nobody could be offered at all. */
  'NO_ELIGIBLE_HOUSE',
  /** BAK-043: the counter gave back an order it had accepted. */
  'COUNTER_GAVE_BACK',
  /** BAK-047: a confirmed closure of a day that had been sold. */
  'CLOSURE',
  /** GST-009: the guest cancelled their own order before a house accepted it. */
  'GUEST',
  /** SUP-008, OPS-002: an operator cancelled it in Internal - the support cancel. */
  'OPERATOR',
  /** BAK-031: the window-end sweep - nobody ever answered the offer. */
  'WINDOW_SWEEP',
  /**
   * BAK-003: a counter accepted it and the window closed with no bag.
   *
   * Its own word since round 3 of the live loop: the stranded sweep has four
   * bands and all four surfaced as `WINDOW_SWEEP`, so the closed set said
   * "the window-end sweep" over a row whose own note said "accepted but never
   * prepared" and only the free text told them apart (order YEY7MG,
   * 2026-09-19).
   */
  'NEVER_PREPARED',
  /**
   * RTE-030 / RTE-019, row 5: the bag was Ready and no courier ever took it -
   * a rider left without it, or one was booked and never came. The courier's
   * by default (founder #4, 5 October 2026).
   */
  'NEVER_COLLECTED',
  /** SUP-003: no courier could be found at all. */
  'NO_COURIER',
  /** The rider reached the door and the handover failed. */
  'DELIVERY_FAILED',
  /** OPS-012: the apartment it was going to has been deleted. */
  'PROPERTY_REMOVED',
  /** Nothing was ever taken: the abandoned checkout, the no-payment-record void. */
  'NEVER_PAID',
] as const;

export const PROVIDER_CAPABILITIES = [
  'SCHEDULED_WINDOWS',
  'TRAVEL_TIME_ESTIMATE',
  'PIN_PROOF',
  'PHOTO_PROOF',
  'SIGNATURE_PROOF',
  'CANCELLATION_WINDOW',
  'TRACKING_URL',
  'QUOTE_EXPIRY',
  'COURIER_TIP',
  'MASKED_CONTACT',
  'COURIER_LOCATION',
  /** A live booking's window can be moved in place, without cancel and re-create. */
  'RESCHEDULE',
  /**
   * The provider listens for "the bag is on the counter" as a fact of its own,
   * separate from the window the booking states (`signalReady`).
   *
   * It is the one thing a counter knows that no provider can derive, and a
   * provider that cannot move a booking's instants may still act on it - which
   * is exactly the trade a provider with no RESCHEDULE makes.
   */
  'READY_SIGNAL',
] as const;

export const REJECTION_REASON_CODES = [
  'OUT_OF_STOCK',
  'CAPACITY_FULL',
  'CLOSED',
  'STAFF_SHORTAGE',
  'EQUIPMENT_FAILURE',
  'OTHER',
] as const;

export const RESPONSIBLE_PARTIES = ['BAKERY', 'GUEST', 'DELIVERY_PROVIDER', 'PLATFORM'] as const;

export const SCHEDULE_SKIP_REASONS = [
  'BAKERY_CLOSED',
  'NO_CAPACITY',
  'NO_BAKERY_AVAILABLE',
  'WRITE_FAILED',
] as const;

export const SHIPPED_LOCALES = ['en', 'fr'] as const;
